<template>
	<div class="shopping-cart-bottom-stepper">
		<v-btn
			v-if="featureToggles.verticalKioskBasket && styleVariant === 'a'"
			@click="$emit('cancelOrdering')"
			class="btn-order cancel-order-btn"
			primary
			flat
			>
			<v-layout class="btn-layout">
				<div>
					<v-icon right dark class="cross">close</v-icon>
					{{ $t('shopComponents.gt3.shoppingCart.shoppingCartBottomStepper.cancelOrder') }}
				</div>
			</v-layout>
		</v-btn>
		<v-btn
			@click="goToCheckout()"
			class="btn-order"
			:loading="sendingOrder"
			:disabled="!cartProducts.length"
			data-apm-span="checkout"
			primary
			flat
			data-gtm-go-to-payment="true"
			data-cy="pay"
			>
			<v-layout class="btn-layout">
				<div>
					{{ $t(`shopComponents.gt3.shoppingCart.shoppingCartBottomStepper.checkout.${channel}`) }}
				</div>
				<div class="total-price" data-cy="productAccumulatedPrice" data-gtm-order-total-price="true">
					{{formatPriceZero(totalPrice)}}
				</div>
			</v-layout>
		</v-btn>
		<mystery-box
			v-if="mysteryBoxProductId"
			:mystery-box-product-id="mysteryBoxProductId"
			:applicable-mystery-box-price-rule="applicableMysteryBoxPriceRule"
			:close-modal="closeMysteryBoxModal"
			@mysteryBoxProductAdded="mysteryBoxProductAdded"
		/>
	</div>
</template>

<script>
	import {mapGetters} from "vuex";
	import appConfig from "../../../../appconfig";
	import * as mutationTypes from "../../../../store/mutation-types";
	import {formatPriceZero} from "../../../../domains2/libraries/Utils";
	import {pushGTMInternal} from "../../../../integrations/tracking/gtm/pushGtmEvents";
	import {prepareGTMeCommerceData} from "../../../../integrations/tracking/gtm/gtmHelper";
	import {pushGTMeCommerceData} from "../../../../integrations/tracking/gtm/pushGTMeCommerceData";
	import { DeliveryMethodsEnum } from "../../../../domains2/libraries/DeliveryMethods";
	import RecommendationEngineProvider from "../../../../providers/RecommendationEngineProvider";
	import MysteryBox from "../../../UtilityComponents/MysteryBox.vue";
	import mysteryBoxMixin from "../../../../mixins/mysteryBox.mixin";
	import {SET_VOUCHER_VIRTUAL_ORDER_ID} from "../../../../store/mutation-types";
	import MysteryBoxProvider from "../../../../providers/MysteryBoxProvider";
	import {handleCartSelectionsWhenProductForPurchaseIsDisabled} from "../../../../helper/CartProductSelectionHelper";

	const {Formats: timeBoxFormats, timebox: timeBox} = appConfig.timeBoxImpl;
	const tenantConfig = appConfig.tenantConfig;

	export default {
		name: "ShoppingCartBottomStepper-gt3",
		components: {MysteryBox},
		props: ["goToPayment", "addToBasket", "preCheckout", "shallUpsellRulesBeShown"],
		mixins: [mysteryBoxMixin],
		data() {
			return {
				sendingOrder: false,
				paymentFlowType: "web",
				mysteryBoxProductId: null,
				applicableMysteryBoxPriceRule: null,
				undefinedProductName: ""
			};
		},
		async mounted() {
			const self = this;
			self.paymentFlowType = appConfig.paymentProcessingLogicHandle();
			const productSelectionWithMysteryBoxVoucher = (this.selections || []).find(ps => ps.mysteryBoxAppliedVoucherId);
			if(productSelectionWithMysteryBoxVoucher && !this.globalSelectedShop.shopOptions.enableMysteryBoxRandomActOfKindness) {
				this.$store.commit(mutationTypes.REMOVE_VOUCHER_FROM_ORDER, productSelectionWithMysteryBoxVoucher.mysteryBoxAppliedVoucherId);
				await this.$store.dispatch(mutationTypes.REMOVE_FROM_CART, productSelectionWithMysteryBoxVoucher.productId);
				this.$store.commit(mutationTypes.SET_MYSTERY_BOX_MODAL_STATE, true);
			}
		},
		computed: {
			...mapGetters([
				"totalPrice",
				"cartProducts",
				"tenantSpecificData",
				"globalSelectedShop",
				"upsellRulesProducts",
				"featureToggles",
				"recommendedProducts",
				"pickedRecommendedProducts",
				"useRecommendations",
				"useCarousel",
				"experimentId",
				"voucherVirtualOrderId"
			]),
			productUndefinedErrorMessage() {
				return this.$t("knownErrors.orderAssortmentRuleBrokenError").replace(/{{\s?productName\s?}}/gi, this.undefinedProductName);
			},
			getPaymentFlowType() {
				const self = this;
				return self.paymentFlowType;
			},
			channel() {
				return appConfig.channelAlias();
			},
			styleVariant() {
				return appConfig.styleVariant;
			},
			shouldRouteToTableServing() {
				const eatInChosen = this.tenantSpecificData.deliveryChoice === DeliveryMethodsEnum.EAT_IN;
				const takeAwayChosen = this.tenantSpecificData.deliveryChoice === DeliveryMethodsEnum.TAKE_OUT;
				return (
					tenantConfig.tableServing &&
					this.globalSelectedShop.shopOptions.tableServing &&
					eatInChosen
				) || (
					this.channel === "kiosk" &&
					this.globalSelectedShop.shopOptions.enableBeeperDeviceFeature &&
					takeAwayChosen
				);
			}
		},
		methods: {
			formatPriceZero,
			pushGTMInternal : pushGTMInternal,
			async setPickupData() {
				const self = this;
				await self.$store.dispatch(mutationTypes.CART_SET_PICKUP_DATA, {
					pickupDate: timeBox.now().format(timeBoxFormats.YYYY_MM_DD).exec(),
					pickupTime: "ASAP",
					submitted: true
				});
				await self.$store.commit(mutationTypes.SET_ASAP_PICKUP, true);
			},
			async sendOrder() {
				const self = this;
				self.sendingOrder = true;

				const baseUrl = window.location.origin;
				const urlAccept = `${baseUrl}/payment/accept`;
				const urlCancel = `${baseUrl}/payment/cancel`;

				self.$store.dispatch(mutationTypes.CART_INITIALIZE_PAYMENT, {
					urlAccept: urlAccept,
					urlCancel: urlCancel
				}).then(async data => {
					const zeroPaymentPath = `/payment/zero?orderNumber=${data.orderNumber}`;
					if (data.payment.paymentType === "zeroPayment") {
						self.sendingOrder = false;
						self.$router.push(zeroPaymentPath);
					} else {
						self.sendingOrder = false;
						if (self.featureToggles.verticalKioskPayment) {
							await self.$router.push("/checkout");
						} else {
							window.location.replace(data.payment.redirectUrl);
						}
					}
				}).catch((err) => {
					self.sendingOrder = false;
					handleCartSelectionsWhenProductForPurchaseIsDisabled(self, err);
					throw err;
				});
			},
			async closeMysteryBoxModal() {
				if(this.voucherVirtualOrderId) {
					await MysteryBoxProvider.changeStatusMysteryBox(this.voucherVirtualOrderId);
					this.$store.commit(SET_VOUCHER_VIRTUAL_ORDER_ID);
				}
				this.$store.commit(mutationTypes.SET_MYSTERY_BOX_MODAL_STATE);
				this.mysteryBoxProductId = null;
				if(this.cartProducts.length) {
					await this.goToCheckout();
				}
			},
			mysteryBoxProductAdded() {
				this.goToCheckout();
			},
			async goToCheckout() {
				const upsellingInCartCount = this.cartProducts.filter(x => Object.keys(this.upsellRulesProducts).includes(x.productId)).length;
				const upsellingProductsCount = Object.keys(this.upsellRulesProducts).length;
				if(await this.getMysteryBoxProduct()) {
					return;
				}
				if ((upsellingInCartCount < upsellingProductsCount
					&& upsellingProductsCount > 0
					&& !this.$store.getters.upsellModalOpenState
					&& this.shallUpsellRulesBeShown
					&& !this.useCarousel)
					|| (this.useRecommendations && !this.useCarousel && !this.$store.getters.upsellModalOpenState)
				) {
					await this.$store.commit(mutationTypes.SET_UPSELL_MODAL_STATE, true);
					this.$emit("showUpsellModal");
				} else {
					const data = prepareGTMeCommerceData();
					pushGTMeCommerceData({
						eventName: "begin_checkout",
						products: data.items,
						value: data.value
					});
					if (
						(this.useRecommendations || this.useCarousel)
						&& this.recommendedProducts.length
						&& this.experimentId
					) {
						RecommendationEngineProvider.sendFeedback({
							recommendations: this.recommendedProducts,
							picks: this.pickedRecommendedProducts,
							channel: appConfig.channelAlias(),
							shopName: this.globalSelectedShop.shopNameAlias,
							experimentId: this.experimentId
						}, appConfig.tenantId);
						this.$store.commit(mutationTypes.SET_RECOMMENDED_PRODUCTS);
						this.$store.commit(mutationTypes.SET_PICKED_RECOMMENDED_PRODUCTS);
						this.$store.commit(mutationTypes.SET_EXPERIMENT_ID);
					}
					if(this.featureToggles.enableRcmProductBadges && this.$store.getters.rcmProductBadgesFeedbackData.recommendations.length) {
						RecommendationEngineProvider.sendFeedbackBadges(
							{
								recommendations: this.$store.getters.rcmProductBadgesFeedbackData.recommendations,
								picks: this.$store.getters.rcmProductBadgesFeedbackData.picks,
								experimentId: `${this.$store.getters.rcmProductBadgesFeedbackData.experimentId}`,
								channel: this.channel,
								shopName: this.globalSelectedShop.shopNameAlias
							},
							appConfig.tenantId
						);
						this.$store.commit(mutationTypes.SET_RCM_PRODUCT_BADGES_PICKS);
					}
					const self = this;
					if (appConfig.channelAlias() === "desktopBrowser") {
						this.pushGTMInternal("Checkout", "visit", "Init");
						self.$router.push("/checkout");
					} else {
						self.sendingOrder = true;
						await self.setPickupData();
						if (self.getPaymentFlowType === "web") {
							if (self.featureToggles.verticalKioskBasket && self.styleVariant === "a" && self.preCheckout) {
								self.$router.push({ name: "pre-checkout" });
							} else
							if (this.shouldRouteToTableServing) {
								self.$router.push("/table-serving");
							} else {
								await self.sendOrder();
							}
						} else {
							if (this.shouldRouteToTableServing) {
								self.$router.push("/table-serving");
							} else {
								self.$router.push("/checkout");
							}
						}
					}
				}
			}
		},
		watch: {
			goToPayment() {
				if (this.goToPayment && !this.addToBasket) {
					this.goToCheckout();
				}
			}
		}
	};
</script>
<style scoped lang="scss">
	.shopping-cart-bottom-stepper{
		position:sticky;
		bottom:0;
		height: 72px;
		margin: 18px 16px;
		.btn-order{
			opacity: 1;
			border-radius: 5px;
			background-color: #218C74 !important;
			height: 100%;
			width: 100%;
			padding: 16px 24px 18px 24px;
			font-family: Dispatch-Bold;
			font-size: 24px;
			font-weight: bold;
			font-stretch: normal;
			font-style: normal;
			line-height: normal;
			letter-spacing: normal;
			color: #ffffff;
			text-transform: capitalize;
			margin: 0;
			.btn-layout {
				display: flex;
				justify-content: space-between;
			}
		}
		.btn-order.v-btn--disabled {
			opacity: 0.3;
		}
	}
</style>
